/**
 * Five-Fold Friendship (Pañca-vargīya Maitri)
 * Calculates personalized planetary relationships based on chart-specific positions
 */

import { FRIENDS, ENEMIES, SIGN_LORDS } from './astroEngine';
import type { PlanetName } from './astroEngine';

// Core planets for friendship calculations (exclude Rahu/Ketu)
const CORE_PLANETS: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

export type FriendshipType = 'great_friend' | 'friend' | 'neutral' | 'enemy' | 'great_enemy';

export interface PlanetFriendship {
  from: PlanetName;
  to: PlanetName;
  naisargika: FriendshipType; // Natural/permanent friendship
  tatkalik: FriendshipType;    // Temporary friendship (based on position)
  panchadha: FriendshipType;   // Combined five-fold relationship
  score: number;                // Numeric score for calculations (-2 to +2)
}

/**
 * Get natural (permanent) friendship between planets
 */
function getNaisargikaFriendship(from: PlanetName, to: PlanetName): FriendshipType {
  if (from === to) return 'great_friend';
  
  const friends = FRIENDS[from] || [];
  const enemies = ENEMIES[from] || [];
  
  if (friends.includes(to)) return 'friend';
  if (enemies.includes(to)) return 'enemy';
  return 'neutral';
}

/**
 * Calculate Tatkalik (temporary) friendship based on house positions
 * Rule: If planet B is in 2nd, 3rd, 4th, 10th, 11th, or 12th from planet A → Friend
 *       Otherwise (1st, 5th, 6th, 7th, 8th, 9th) → Enemy
 */
function getTatkalikFriendship(fromHouse: number, toHouse: number): FriendshipType {
  if (fromHouse === toHouse) return 'great_friend'; // Same house = great friend
  
  // Calculate house offset (1-12)
  let offset = toHouse - fromHouse;
  if (offset < 0) offset += 12;
  if (offset === 0) offset = 12;
  
  // Friendly houses: 2, 3, 4, 10, 11, 12
  const friendlyHouses = [2, 3, 4, 10, 11, 12];
  
  if (friendlyHouses.includes(offset)) {
    return 'friend';
  } else {
    return 'enemy';
  }
}

/**
 * Combine Naisargika and Tatkalik to get Panchadha (five-fold) relationship
 * 
 * Combination rules:
 * - Friend + Friend = Great Friend (Adhi Mitra)
 * - Friend + Neutral = Friend
 * - Friend + Enemy = Neutral
 * - Neutral + Neutral = Neutral
 * - Neutral + Enemy = Enemy
 * - Enemy + Enemy = Great Enemy (Adhi Satru)
 */
function getPanchadhaFriendship(naisargika: FriendshipType, tatkalik: FriendshipType): FriendshipType {
  const scoreMap: Record<FriendshipType, number> = {
    'great_friend': 2,
    'friend': 1,
    'neutral': 0,
    'enemy': -1,
    'great_enemy': -2
  };
  
  const combinedScore = scoreMap[naisargika] + scoreMap[tatkalik];
  
  if (combinedScore >= 2) return 'great_friend';
  if (combinedScore === 1) return 'friend';
  if (combinedScore === 0) return 'neutral';
  if (combinedScore === -1) return 'enemy';
  return 'great_enemy';
}

/**
 * Convert friendship type to numeric score for calculations
 */
export function friendshipToScore(friendship: FriendshipType): number {
  const scoreMap: Record<FriendshipType, number> = {
    'great_friend': 2,
    'friend': 1,
    'neutral': 0,
    'enemy': -1,
    'great_enemy': -2
  };
  return scoreMap[friendship];
}

/**
 * Calculate five-fold friendships for all planet pairs in a chart
 */
export function calculateFiveFoldFriendships(
  placements: Partial<Record<PlanetName, { house: number }>>
): PlanetFriendship[] {
  const friendships: PlanetFriendship[] = [];
  
  for (const from of CORE_PLANETS) {
    const fromHouse = placements[from]?.house;
    if (!fromHouse) continue;
    
    for (const to of CORE_PLANETS) {
      if (from === to) continue; // Skip self
      
      const toHouse = placements[to]?.house;
      if (!toHouse) continue;
      
      const naisargika = getNaisargikaFriendship(from, to);
      const tatkalik = getTatkalikFriendship(fromHouse, toHouse);
      const panchadha = getPanchadhaFriendship(naisargika, tatkalik);
      
      friendships.push({
        from,
        to,
        naisargika,
        tatkalik,
        panchadha,
        score: friendshipToScore(panchadha)
      });
    }
  }
  
  return friendships;
}

/**
 * Get friendship from one planet to another
 */
export function getFriendship(
  friendships: PlanetFriendship[],
  from: PlanetName,
  to: PlanetName
): PlanetFriendship | undefined {
  return friendships.find(f => f.from === from && f.to === to);
}

/**
 * Get all friendships for a specific planet
 */
export function getPlanetFriendships(
  friendships: PlanetFriendship[],
  planet: PlanetName
): PlanetFriendship[] {
  return friendships.filter(f => f.from === planet);
}

/**
 * Get friendship matrix as a 2D structure for display
 */
export function getFriendshipMatrix(
  friendships: PlanetFriendship[]
): Record<PlanetName, Record<PlanetName, PlanetFriendship>> {
  const matrix = {} as Record<PlanetName, Record<PlanetName, PlanetFriendship>>;
  
  for (const planet of CORE_PLANETS) {
    matrix[planet] = {} as Record<PlanetName, PlanetFriendship>;
  }
  
  for (const friendship of friendships) {
    matrix[friendship.from][friendship.to] = friendship;
  }
  
  return matrix;
}
